import { Anchor, Badge, Divider, Drawer, Group, List, Stack, Text } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import type { NutritionVariant, Reference } from '@mdev/common';

interface GeneDrawerProps {
  gene: string;
  variants: NutritionVariant[];
  references: Reference[];
  isOpen: boolean;
  onClose: () => void;
}

const GENE_DESCRIPTIONS: Record<string, string> = {
  FUT2: 'Fucosyltransferase 2 - determines secretor status affecting B12 absorption and gut microbiome composition',
  TCN2: 'Transcobalamin 2 - primary B12 transport protein in blood',
  MTR: 'Methionine synthase - catalyzes the remethylation of homocysteine to methionine',
  MTRR: 'Methionine synthase reductase - maintains MTR in active state',
  MTHFR: 'Methylenetetrahydrofolate reductase - key enzyme in folate metabolism',
  PEMT: 'Phosphatidylethanolamine N-methyltransferase - synthesizes phosphatidylcholine from phosphatidylethanolamine',
  CHDH: 'Choline dehydrogenase - first enzyme in choline oxidation pathway',
  MTHFD1: 'Methylenetetrahydrofolate dehydrogenase 1 - trifunctional enzyme in folate metabolism',
  MTHFD1L: 'Methylenetetrahydrofolate dehydrogenase 1-like - mitochondrial folate enzyme',
  GC: 'Group-specific component - vitamin D binding protein, main carrier of vitamin D',
  CYP2R1: 'Cytochrome P450 2R1 - primary vitamin D 25-hydroxylase',
  DHCR7: '7-dehydrocholesterol reductase - affects vitamin D synthesis',
  CYP24A1: 'Cytochrome P450 24A1 - vitamin D 24-hydroxylase, catabolizes vitamin D',
  SLC23A1: 'Solute carrier family 23 member 1 - sodium-dependent vitamin C transporter',
  HFE: 'Homeostatic iron regulator - controls iron absorption in the intestine',
  TMPRSS6: 'Transmembrane protease serine 6 - regulates hepcidin expression',
  TF: 'Transferrin - iron transport protein in blood',
  FADS1: 'Fatty acid desaturase 1 - introduces double bonds into fatty acids',
  FADS2: 'Fatty acid desaturase 2 - converts linoleic acid to arachidonic acid',
  ELOVL2: 'Elongation of very long chain fatty acids protein 2 - elongates omega-3 fatty acids',
  APOE: 'Apolipoprotein E - major component of lipoproteins, affects lipid metabolism',
  SOD2: 'Superoxide dismutase 2 - mitochondrial antioxidant enzyme',
  GPX1: 'Glutathione peroxidase 1 - reduces hydrogen peroxide using glutathione',
  NQO1: 'NAD(P)H quinone dehydrogenase 1 - protects against quinone toxicity',
  NOS3: 'Nitric oxide synthase 3 - produces nitric oxide for vascular function',
  MCM6: 'Minichromosome maintenance complex component 6 - regulates lactase expression',
  FTO: 'FTO alpha-ketoglutarate dependent dioxygenase - affects appetite and energy balance',
  MC4R: 'Melanocortin 4 receptor - central regulator of energy homeostasis',
  PPARG: 'Peroxisome proliferator activated receptor gamma - transcription factor regulating metabolism',
};

const GENE_RELEVANCE: Record<string, string> = {
  FUT2: 'Secretor status affects B12 requirements and may influence supplement absorption efficiency',
  TCN2: 'Variants may affect B12 transport and cellular uptake, potentially influencing B12 status',
  MTR: 'Variants may increase homocysteine levels and affect methylation capacity',
  MTRR: 'Variants may reduce enzyme activity and affect B12-dependent methylation',
  MTHFR: 'Common variants significantly affect folate requirements and homocysteine levels',
  PEMT: 'Variants affect choline requirements, especially important during pregnancy',
  GC: 'Variants affect vitamin D transport and may influence vitamin D requirements',
  CYP2R1: 'Variants may affect vitamin D activation efficiency',
  HFE: 'Variants associated with hemochromatosis and increased iron absorption',
  FADS1: 'Variants affect omega-3 fatty acid status and may influence dietary requirements',
  FADS2: 'Variants affect conversion of essential fatty acids to longer-chain metabolites',
  SOD2: 'Variants may affect antioxidant capacity and oxidative stress susceptibility',
  MCM6: 'Determines adult lactose tolerance - important for dairy consumption',
};

export function GeneDrawer({ gene, variants, references, isOpen, onClose }: GeneDrawerProps) {
  const found = variants.filter((v) => v.genotype !== '--');
  const missing = variants.filter((v) => v.genotype === '--');

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      title={`${gene} Gene Information`}
      position="right"
      size="lg"
    >
      <Stack gap="md">
        <div>
          <Text fw={600} mb="xs">
            Gene Function
          </Text>
          <Text size="sm" c="dimmed">
            {GENE_DESCRIPTIONS[gene] ?? 'Gene function information not available'}
          </Text>
        </div>

        <Divider />

        <div>
          <Text fw={600} mb="xs">
            Coverage Summary
          </Text>
          <Group gap="md">
            <Badge color="green" variant="light">
              {found.length} Found
            </Badge>
            <Badge color="gray" variant="light">
              {missing.length} Missing
            </Badge>
          </Group>
        </div>

        {found.length > 0 && (
          <div>
            <Text fw={600} mb="xs">
              Found Variants
            </Text>
            <List size="sm">
              {found.map((variant) => (
                <List.Item key={variant.rsid}>
                  <Group justify="space-between">
                    <div>
                      <Text fw={500}>
                        {variant.rsid} ({variant.variantName})
                      </Text>
                      <Text size="xs" c="dimmed">
                        Genotype: {variant.genotype} | Result: {variant.zygosity}
                      </Text>
                    </div>
                    {variant.evidenceLevel && (
                      <Badge size="xs" variant="light">
                        {variant.evidenceLevel}
                      </Badge>
                    )}
                  </Group>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        {missing.length > 0 && (
          <div>
            <Text fw={600} mb="xs">
              Missing Variants
            </Text>
            <List size="sm">
              {missing.map((variant) => (
                <List.Item key={variant.rsid}>
                  <Text c="dimmed">
                    {variant.rsid} ({variant.variantName}) - Not found in your data
                  </Text>
                </List.Item>
              ))}
            </List>
          </div>
        )}

        <Divider />

        {references.length > 0 && (
          <div>
            <Text fw={600} mb="xs">
              Primary Research
            </Text>
            <Stack gap="xs">
              {references.map((ref, i) => (
                <Anchor key={ref.url} href={ref.url} target="_blank" size="sm">
                  <Group gap={4}>
                    <Text>{ref.title || `PubMed Reference ${i + 1}`}</Text>
                    <IconExternalLink size={14} />
                  </Group>
                </Anchor>
              ))}
            </Stack>
          </div>
        )}

        <div>
          <Text fw={600} mb="xs">
            Clinical Relevance
          </Text>
          <Text size="sm" c="dimmed">
            {GENE_RELEVANCE[gene] ?? 'Clinical relevance varies by individual genetic context'}
          </Text>
        </div>
      </Stack>
    </Drawer>
  );
}
